import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './SidebarLayout.module.css';
import Header from '../../Header/Header';
import SidebarMenu from '../../SidebarMenu/SidebarMenu';

interface ISidebarLayoutProps {
    className?: string;
    children: ReactNode;
}

const SidebarLayout: FunctionComponent<ISidebarLayoutProps> = ({
    className,
    children,
}) => {
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [scrollMenu, setScrollMenu] = useState<boolean>(false);

    const sidebarRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // Detect clicks outside sidebar
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(event.target as Node)
            ) {
                setShowMenu(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    // Detect when content touches top
    useEffect(() => {
        const contentEl = contentRef.current;
        if (!contentEl) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                // When it's not intersecting with the viewport top → it's scrolled
                setScrollMenu(!entry.isIntersecting);
            },
            {
                root: null, // viewport
                threshold: 0, // fire as soon as it leaves/touches
                rootMargin: "0px 0px -100% 0px" // triggers when top reaches viewport top
            }
        );

        observer.observe(contentEl);

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <>
            <Header menuClick={(e) => {
                e.stopPropagation(); 
                setShowMenu(!showMenu);
            }} />
            <main className={cn(styles.sidebarMenuLayout, className)}>
                <div className={styles.sidebarMenuLayoutRow}>
                    <div ref={sidebarRef} className={cn(styles.sidebar, showMenu && styles.showSidebar, scrollMenu && styles.scrolled)}>
                        <div className={cn(styles.sidebarIn)}>
                            <div className={cn(styles.sidebarInMenu)}>
                                <SidebarMenu />
                            </div>
                        </div>
                    </div>
                    <div ref={contentRef} className={styles.content}>
                        {children}
                    </div>
                </div>
            </main>
        </>
    );
};

export default SidebarLayout;
